import { Link } from 'react-router'
import InlineSvg from 'svg-inline-react'
import Select from 'react-select-plus'
import { TextArea, Input, Button, Icon, ToolTip } from '@driftt/tide-core-legacy'
import CopyToClipboard from 'react-copy-to-clipboard'

import CalendarSettingsPage from '../page'

import { MEETING_ICON_TO_DURATION, CUSTOM_MEETING_LENGTH_OPTIONS } from '../constants'

import infoIconSrc from '!svg-inline?removeTags=true!images/svg/info-icon.svg'

import './style.styl'

const INFO_SLUG_CHANGE_COPY = "Changing your meeting link will not automatically update \r\nany links you've shared outside of Drift. Change this with care!"

const HelpIcon = ({ copy }) => (
  <div className="inline-help-icon-tooltip">
    <ToolTip
      flow={'up'}
      component={<span>{copy}</span>}>
      <InlineSvg src={infoIconSrc} style={{ display: 'block' }} />
    </ToolTip>
  </div>
)

const MEETING_NOTIFICATION_COPY = Object.freeze({
  confirmation: 'Automatic confirmation emails for your guests are enabled.',
  reminder: 'Automatic reminder emails for your guests are enabled.',
  both: 'Automatic confirmation and reminder emails for your guests are enabled.'
})

const MeetingTypeEdit = ({
  settings,
  isDefault,
  isCopied,
  isSaving,
  profileUrl,
  handleUpdateMeetingName,
  handleDurationSelect,
  handleChangeMeetingSlug,
  handleUpdateMeetingLocation,
  handleUpdateMeetingDescription,
  isSlugInConflict,
  isSlugEmpty,
  isSlugReserved,
  isSlugValid,
  isSlugChanged,
  isTitleEmpty,
  handleCopyMeetingLink,
  isSettingsChanged,
  isTitleChanged,
  handleSave,
  placeholderSlug,
  confirmationEmailStatus,
  reminderEmailStatus,
}) => {
  const meetingLink = `${profileUrl}${isDefault ? '?schedule' : `/${settings.slug}`}`
  const meetingNotificationCopy = (confirmationEmailStatus && reminderEmailStatus)
    ? MEETING_NOTIFICATION_COPY.both
    : confirmationEmailStatus
      ? MEETING_NOTIFICATION_COPY.confirmation
      : MEETING_NOTIFICATION_COPY.reminder
  return (
    <CalendarSettingsPage hideHeader={true}>
      <div className="meeting-type-edit-wrapper tide-form">
        <div className="header">
          <Link className="settings-nav" to="/settings/calendar">
            <Icon className="settings-nav-back-bracket" width={6} name="arrow-1-left" />
            <div>Back to Calendar Settings</div>
          </Link>
          <div className="meeting-type-edit-section">
            <img
              className="meeting-type-edit-duration-icon"
              src={MEETING_ICON_TO_DURATION[`${settings.duration}`]}
            />
            <div className="meeting-type-edit-duration">
              <div className="meeting-type-edit-subheader">Meeting Duration</div>
              <Select
                className="meeting-type-edit-duration-select"
                value={settings.duration}
                options={CUSTOM_MEETING_LENGTH_OPTIONS}
                onChange={handleDurationSelect}
                clearable={false}
                searchable={false}
              />
            </div>
          </div>
        </div>

        <div className="meeting-type-edit-setting">
          <div className="meeting-type-edit-control">
            <Input
              type="text"
              label={
                <div>
                  Public Calendar Event Title <span className="muted">(required)</span>
                </div>
              }
              className="meeting-type-input"
              defaultValue={settings.name}
              onChange={handleUpdateMeetingName}
              placeholder={`${settings.duration} Minute Meeting`}
            />
            {isTitleEmpty && isTitleChanged ? (
              <div className="meeting-type-edit-helptext error">
                Please fill out a name for your meeting type. It will be used when people book time with you.
              </div>
            ) : (
              <div className="meeting-type-edit-description meeting-type-edit-helptext">
                This should describe the type of meeting people are booking with you.
              </div>
            )}
          </div>
        </div>

        <div className="meeting-type-edit-setting">
          <div className="meeting-type-edit-subheader">
            <div className="meeting-type-subheader-with-info">
              Meeting Link
              <span className="muted">
                {isDefault ? ' (the default link cannot be changed)' : ' (required)'}
              </span>
              {!isDefault && <HelpIcon copy={INFO_SLUG_CHANGE_COPY} />}
            </div>
          </div>
          <div className="meeting-type-edit-control">
            {isDefault ? (
              <input type="text" className="tide-input" value={`${profileUrl}?schedule`} disabled={true} />
            ) : (
              <div className="meeting-type-edit-slug-wrapper">
                <div className="faux-input-wrapper">
                  <span className="faux-input-prefix">{profileUrl}/</span>
                  <input
                    type="text"
                    className="tide-input"
                    autoCorrect="off"
                    value={settings.slug}
                    placeholder={`${settings.duration}m-${placeholderSlug}`}
                    onChange={handleChangeMeetingSlug}
                  />
                </div>
                <CopyToClipboard text={meetingLink} onCopy={handleCopyMeetingLink}>
                  <Button
                    className="meeting-link-copy"
                    type="tertiary"
                    disabled={!isSlugValid && isSlugChanged}
                    onClick={handleCopyMeetingLink}
                  >
                    <Icon className="meeting-link-copy-icon" width={12} name="link-1" />{' '}
                    {isCopied ? 'Copied!' : 'Copy Link'}
                  </Button>
                </CopyToClipboard>
              </div>
            )}
            {isSlugInConflict && (
              <div className="meeting-type-edit-helptext error">This link is already in use.</div>
            )}
            {isSlugChanged && isSlugEmpty && (
              <div className="meeting-type-edit-helptext error">Meeting link cannot be empty.</div>
            )}
            {isSlugReserved && (
              <div className="meeting-type-edit-helptext error">Meeting link cannot be a reserved word.</div>
            )}
          </div>
        </div>

        <div className="meeting-type-edit-setting">
          <div className="meeting-type-edit-control">
            <Input
              type="text"
              label="Location"
              className="meeting-type-input"
              defaultValue={settings.location}
              onChange={handleUpdateMeetingLocation}
              placeholder="phone call, zoom.us, join.me, Skype, 123 Drift Street"
            />
          </div>
          <div className="meeting-type-edit-helptext">This is where your meeting will take place.</div>
        </div>

        <div className="meeting-type-edit-setting">
          <div className="meeting-type-edit-control">
            <TextArea
              label="Description / Meeting Instructions"
              placeholder="Describe what your attendees can expect from this meeting"
              onChange={handleUpdateMeetingDescription}
              defaultValue={settings.inviteDescription}
            />
          </div>
        </div>
        <div className="meeting-notification-copy-wrapper">
          <div className="meeting-notification-header">Meeting Notifications</div>
          <div className="meeting-notification-copy">
            {meetingNotificationCopy} <Link to="/settings/calendar/advanced">View Calendar Settings</Link>
          </div>
        </div>
        <Button
          className="meeting-type-edit-save-button"
          onClick={handleSave}
          type="primary"
          disabled={!isSettingsChanged || isSaving || !isSlugValid || isTitleEmpty}
        >
          {isSaving ? 'Saving...' : 'Save & Finish'}
        </Button>
      </div>
    </CalendarSettingsPage>
  )
}

export default MeetingTypeEdit



// WEBPACK FOOTER //
// ./src/scripts/components/settings/meetings/MeetingTypeEdit/component.js